import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { List, Text, Column, Row, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { AccessSubjectTypes, AccessTypes, type IAccessControlModalsRef, type IUser } from '@oliasoft/hierarchy-gui';
import { ability, ActionMap } from '@oliasoft/authorization-ui';
import { useParams } from 'react-router';
import translations from 'src/internationalisation/translation-map.json';
import { selectPriceBookAdding, selectPriceBooks } from 'src/store/entities/price-book/selectors';
import { addPriceBook, removePriceBook } from 'src/store/entities/price-book/price-book';
import { IPriceBook } from 'src/common/interfaces/price-book.interfaces';
import { nextName } from 'client/common/utils/next-name';
import { CaslEnums } from 'src/enums/casl-enums';
import { makeAccessControlOption } from 'src/common/hooks/use-access-control-menu';
import { validatePriceBookAccess } from 'src/common/access-control/pricebook-access-validator';
import { getEntityAccessRightsList } from 'src/store/entities/access-control/access-control';
import type { TRootState } from 'src/store/store-types';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';
import { PriceBookRenameModal } from './price-book-rename.modal';

type TPriceBookProps = PropsFromRedux & {
  selectedPriceBook: { priceBookId: string; name: string } | null;
  onClickPriceBook: (arg: { priceBookId: string; name: string; isPriceBookAccessible: boolean } | null) => void;
  accessControlChildRef: React.RefObject<IAccessControlModalsRef | null>;
  getEntityAccessRightsList: (type: string, id: string, subjectType: AccessSubjectTypes) => void;
  users: IUser[];
  apiKeys?: string[];
  currentUserId: string;
};

const PriceBooksList = ({
  isAdding,
  priceBooks,
  onClickPriceBook,
  selectedPriceBook,
  addPriceBook,
  removePriceBook,
  accessControlChildRef,
  entityAccessRightsList,
  getEntityAccessRightsList,
  users,
  apiKeys,
  pricebookAccessRights,
  currentUserId,
}: TPriceBookProps) => {
  const { t } = useTranslation();
  const { company } = useParams();

  const [priceBookForRename, setPriceBookForRename] = useState<IPriceBook | null>(null);

  const onClickRenamePriceBook = (data: IPriceBook | null) => {
    setPriceBookForRename(data);
  };

  const onRemovePriceBook = (id: string) => {
    removePriceBook(id);
    onClickPriceBook(null);
  };

  const priceBooksList = useMemo(() => {
    if (!priceBooks) return [];

    return priceBooks.map((item) => {
      const priceBookAccessData = pricebookAccessRights?.find((p: any) => p.id === item.priceBookId);
      const isPriceBookAccessible = validatePriceBookAccess(currentUserId, priceBookAccessData, item.subjectId);

      const hasAdminAccess = ability.can(ActionMap.MANAGE, CaslEnums.Access);
      const isPriceBookOwner = currentUserId === item.subjectId;
      const hasAccessControlSet = priceBookAccessData?.accessRights && priceBookAccessData.accessRights.length > 0;
      const isPublicAccess = priceBookAccessData?.metadata?.accesstype === AccessTypes.All;

      const accessControlOption = makeAccessControlOption({
        t,
        item: { ...item, id: item.priceBookId, entityType: 'pricebook' },
        subjectType: AccessSubjectTypes.User,
        icon: 'key',
        accessControlChildRef,
        users,
        apiKeys,
        getEntityAccessRightsList,
      });

      const getItemIcon = () => {
        if (!isPriceBookAccessible) {
          return {
            icon: 'lock',
            color: 'var(--color-error-400)',
            tooltip: { text: t(translations.accessControl_accessRestricted) },
          };
        }
        if (hasAccessControlSet && !isPublicAccess && (hasAdminAccess || isPriceBookOwner)) {
          return {
            icon: 'lock',
            color: 'var(--color-success-600)',
            tooltip: { text: t(translations.accessControl_accessIsSet) },
          };
        }
        return undefined;
      };

      return {
        key: `${item.priceBookId}-${hasAccessControlSet}-${isPublicAccess}`,
        id: item.priceBookId!,
        name: item.name,
        testId: `price-books-list-${convertToKebabCase(item.name)}-item`,
        icon: getItemIcon(),
        active: item.priceBookId === selectedPriceBook?.priceBookId,
        onClick: () =>
          onClickPriceBook({
            priceBookId: item.priceBookId!,
            name: item.name,
            isPriceBookAccessible,
          }),
        actions: isPriceBookAccessible
          ? [
              {
                label: 'More',
                testId: `price-books-list-${convertToKebabCase(item.name)}-more-button`,
                subActions: [
                  {
                    icon: 'delete',
                    label: t(translations.delete),
                    onClick: () => onRemovePriceBook(item.priceBookId!),
                    testId: `price-books-list-${convertToKebabCase(item.name)}-delete-button`,
                  },
                  {
                    icon: 'edit',
                    label: t(translations.rename),
                    onClick: () =>
                      onClickRenamePriceBook({
                        priceBookId: item.priceBookId!,
                        name: item.name,
                        subjectId: item.subjectId,
                      }),
                    testId: `price-books-list-${convertToKebabCase(item.name)}-rename-button`,
                  },
                  ...(accessControlOption && (hasAdminAccess || isPriceBookOwner) ? [accessControlOption] : []),
                ],
              },
            ]
          : [],
      };
    });
  }, [priceBooks, pricebookAccessRights, selectedPriceBook, currentUserId, t]);

  useEffect(() => {
    accessControlChildRef.current?.accessRightListUpdated({ accessRightsList: entityAccessRightsList, metadata: [] });
  }, [entityAccessRightsList]);

  return (
    <div>
      <List
        testId="price-books-list"
        list={{
          name: (
            <Column>
              <Row>
                <Text>{t(translations.priceBook)}</Text>
              </Row>
            </Column>
          ),
          actions: [
            {
              label: 'Add',
              icon: 'add',
              onClick: () =>
                company &&
                addPriceBook(
                  nextName(
                    priceBooksList.map(({ name }) => name),
                    'New Price Book',
                  ),
                  company,
                  currentUserId,
                ),
              disabled: isAdding,
              testId: 'price-books-list-subheading-add-button',
            },
          ],
          items: priceBooksList,
        }}
      />
      {priceBookForRename && (
        <PriceBookRenameModal selectedPriceBook={priceBookForRename} onClickRenamePriceBook={onClickRenamePriceBook} />
      )}
    </div>
  );
};

const mapStateToProps = ({ entities }: TRootState) => ({
  isAdding: selectPriceBookAdding(entities),
  priceBooks: selectPriceBooks(entities),
  entityAccessRightsList: entities?.accessControl.accessRights,
  pricebookAccessRights: entities?.accessControl.pricebookAccessRights,
  users: entities?.user.users,
  apiKeys: entities?.accessControl.apiKeys,
});

const mapDispatchToProps = {
  addPriceBook,
  removePriceBook,
  getEntityAccessRightsList,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(PriceBooksList));

export { Container as PriceBooksList };
